import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  useJsApiLoader, 
  GoogleMap, 
  Marker, 
  InfoWindow, 
  Polyline 
} from '@react-google-maps/api';
import PropTypes from 'prop-types';

// Configuración centralizada de Google Maps
const GOOGLE_MAPS_CONFIG = {
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: ["geometry", "drawing", "places", "routes"],
  id: "google-maps-script", // ID único para toda la aplicación
  version: "weekly",
  language: "es",
  region: "US",
  nonce: "",
  url: "https://maps.googleapis.com/maps/api/js",
  authReferrerPolicy: "origin"
};

const GoogleMapsContext = createContext(null);

export const GoogleMapsProvider = ({ children }) => {
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_CONFIG);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [forceUnmount, setForceUnmount] = useState(false);

  // Actualizar estado cuando la API está cargada
  useEffect(() => {
    if (isLoaded) {
      setGoogleMapsLoaded(true);
    }
  }, [isLoaded]);

  // Método para forzar descarga y recarga de mapas cuando hay problemas
  const resetGoogleMaps = () => {
    setGoogleMapsLoaded(false);
    setForceUnmount(true);
    
    // Volver a activar después de un tiempo breve
    setTimeout(() => {
      setForceUnmount(false);
      if (isLoaded) {
        setGoogleMapsLoaded(true);
      }
    }, 500);
  };

  const value = {
    isLoaded,
    loadError,
    googleMapsLoaded,
    resetGoogleMaps,
    forceUnmount,
    // Exportar componentes para uso interno
    components: {
      GoogleMap,
      Marker,
      InfoWindow,
      Polyline
    }
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

GoogleMapsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error('useGoogleMaps debe usarse dentro de un GoogleMapsProvider');
  }
  return context;
};

// Exportar componentes de Google Maps directamente desde el contexto
// Esto permite importar desde un solo lugar en toda la aplicación
export { GoogleMap, Marker, InfoWindow, Polyline, OverlayView } from '@react-google-maps/api';